import React, { useState } from 'react'
import { useMutation } from '@apollo/client'
import {
  TextField,
  Button,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Divider,
  Chip,
  Stack,
} from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { CREATE_GROUP_COACHING_EVENT } from 'Apollo/Mutations/CreateGroupCoachingEvent'
import {
  Container,
  Form,
  Section,
  SuccessView,
  ErrorMessage,
} from './CoachingEventCreation.styled'
import { ActivityType } from '../../../../Generated/graphql-types'
import { generateSlug } from './coachingEventutils'

interface CoachingEventCreationProps {
  popError: (message: string) => void
  popSuccess: (message: string) => void
}

export const CoachingEventCreation: React.FC<CoachingEventCreationProps> = ({
  popError,
  popSuccess,
}) => {
  // Calculate one day in the future for default start date
  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)

  // Calculate one hour after start time for default end date
  const defaultEndDate = new Date(tomorrow)
  defaultEndDate.setHours(defaultEndDate.getHours() + 1)

  const [formData, setFormData] = useState({
    name: '',
    startsAt: tomorrow,
    endsAt: defaultEndDate,
    hideAttendees: false,
    activityType: ActivityType.GroupCoaching,
    duration: 60,
    body: '',
    focusAreas: [] as string[],
    coachName: '',
    coachEmail: '',
  })

  const [focusAreaInput, setFocusAreaInput] = useState('')
  const [createdEvent, setCreatedEvent] = useState<any>(null)

  const [createCoachingEvent, { loading, error }] = useMutation(
    CREATE_GROUP_COACHING_EVENT,
    {
      onCompleted: (data) => {
        setCreatedEvent(data.createCoachingEvent)
        popSuccess('Coaching event created successfully!')
      },
      onError: (err) => {
        popError(`Error creating coaching event: ${err.message}`)
      },
    }
  )

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>
  ) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name as string]: value }))
  }

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target
    setFormData((prev) => ({ ...prev, [name]: checked }))
  }

  const handleDateChange = (field: string, value: Date | null) => {
    if (value) {
      setFormData((prev) => ({ ...prev, [field]: value }))

      // If setting start date, update end date to be 1 hour later
      if (field === 'startsAt') {
        const newEndDate = new Date(value)
        newEndDate.setHours(newEndDate.getHours() + 1)
        setFormData((prev) => ({ ...prev, endsAt: newEndDate }))
      }
    }
  }

  const handleFocusAreaInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFocusAreaInput(e.target.value)
  }

  const handleFocusAreaInputKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addFocusArea()
    }
  }

  const addFocusArea = () => {
    // Split by commas and process each area
    const areas = focusAreaInput
      .split(',')
      .map((area) => area.trim())
      .filter((area) => area.length > 0)

    if (areas.length > 0) {
      // Add new areas (avoiding duplicates)
      setFormData((prev) => ({
        ...prev,
        focusAreas: [...new Set([...prev.focusAreas, ...areas])],
      }))
      setFocusAreaInput('')
    }
  }

  const handleFocusAreaDelete = (areaToDelete: string) => {
    setFormData((prev) => ({
      ...prev,
      focusAreas: prev.focusAreas.filter((area) => area !== areaToDelete),
    }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    // Validate required fields
    if (!formData.coachName.trim()) {
      popError('Coach Name is required')
      return
    }

    if (!formData.coachEmail.trim()) {
      popError('Coach Email is required')
      return
    }

    // Calculate duration if not set manually
    const durationInMinutes =
      formData.duration ||
      Math.round(
        (formData.endsAt.getTime() - formData.startsAt.getTime()) / 60000
      )

    // Generate a slug if not provided
    const slug = generateSlug(formData.name)

    // Join focus areas into a comma-separated string
    const focusArea = formData.focusAreas.join(', ')

    createCoachingEvent({
      variables: {
        input: {
          name: formData.name,
          startsAt: formData.startsAt,
          endsAt: formData.endsAt,
          hideAttendees: formData.hideAttendees,
          activityType: ActivityType.GroupCoaching,
          duration: parseInt(String(durationInMinutes), 10),
          body: formData.body,
          focusArea,
          coachName: formData.coachName,
          coachEmail: formData.coachEmail,
          slug,
        },
      },
    })
  }

  const resetForm = () => {
    const newTomorrow = new Date()
    newTomorrow.setDate(newTomorrow.getDate() + 1)

    const newEndDate = new Date(newTomorrow)
    newEndDate.setHours(newEndDate.getHours() + 1)

    setFormData({
      name: '',
      startsAt: newTomorrow,
      endsAt: newEndDate,
      hideAttendees: false,
      activityType: ActivityType.GroupCoaching,
      duration: 60,
      body: '',
      focusAreas: [],
      coachName: '',
      coachEmail: '',
    })
    setFocusAreaInput('')
    setCreatedEvent(null)
  }

  if (createdEvent) {
    return (
      <SuccessView>
        <Typography variant='h5'>Coaching Event Created</Typography>
        <Section>
          <Typography variant='h6'>Event Details</Typography>
          <Box>
            <Typography>
              <strong>Name:</strong> {createdEvent.name}
            </Typography>
            <Typography>
              <strong>Start:</strong>{' '}
              {new Date(createdEvent.startsAt).toLocaleString()}
            </Typography>
            <Typography>
              <strong>End:</strong>{' '}
              {new Date(createdEvent.endsAt).toLocaleString()}
            </Typography>
            <Typography>
              <strong>Duration:</strong> {createdEvent.duration} minutes
            </Typography>
            <Typography>
              <strong>Type:</strong> Group Coaching
            </Typography>
            <Typography>
              <strong>Coach:</strong> {createdEvent.coachName}
            </Typography>
            <Typography>
              <strong>Coach Email:</strong> {createdEvent.coachEmail}
            </Typography>
            {createdEvent.focusArea && (
              <Typography>
                <strong>Focus Areas:</strong> {createdEvent.focusArea}
              </Typography>
            )}
            {createdEvent.bookingUrl && (
              <Typography>
                <strong>Circle Booking URL:</strong> {createdEvent.bookingUrl}
              </Typography>
            )}
            {createdEvent.googleCalendarLink && (
              <Typography>
                <strong>Calendar Link:</strong>{' '}
                {createdEvent.googleCalendarLink}
              </Typography>
            )}
            {createdEvent.videoConferenceUrl && (
              <Typography>
                <strong>Meeting Link:</strong> {createdEvent.videoConferenceUrl}
              </Typography>
            )}
          </Box>
        </Section>
        <Button variant='contained' color='primary' onClick={resetForm}>
          Create Another Event
        </Button>
      </SuccessView>
    )
  }

  return (
    <Container>
      <Typography variant='h5'>Create Coaching Event</Typography>
      <Form onSubmit={handleSubmit}>
        <Section>
          <Typography variant='h6'>Basic Information</Typography>
          <TextField
            label='Event Name'
            name='name'
            value={formData.name}
            onChange={handleChange}
            fullWidth
            required
            margin='normal'
          />

          <Box sx={{ mt: 2 }}>
            <Typography variant='body2' color='textSecondary' gutterBottom>
              Focus Areas (comma-separated)
            </Typography>

            {formData.focusAreas.length > 0 && (
              <Stack
                direction='row'
                spacing={1}
                sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}
              >
                {formData.focusAreas.map((area) => (
                  <Chip
                    key={area}
                    label={area}
                    onDelete={() => handleFocusAreaDelete(area)}
                    size='small'
                  />
                ))}
              </Stack>
            )}

            <TextField
              placeholder='Enter focus areas (separate with comma)'
              value={focusAreaInput}
              onChange={handleFocusAreaInputChange}
              onKeyDown={handleFocusAreaInputKeyDown}
              onBlur={addFocusArea}
              fullWidth
              margin='normal'
            />
          </Box>
        </Section>

        <Section>
          <Typography variant='h6'>Scheduling</Typography>
          <Box display='flex' gap={2} flexWrap='wrap'>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker
                label='Start Date & Time'
                value={formData.startsAt}
                onChange={(date) => handleDateChange('startsAt', date)}
                sx={{ minWidth: '240px' }}
              />

              <DateTimePicker
                label='End Date & Time'
                value={formData.endsAt}
                onChange={(date) => handleDateChange('endsAt', date)}
                sx={{ minWidth: '240px' }}
              />
            </LocalizationProvider>
          </Box>

          <TextField
            label='Duration (minutes)'
            name='duration'
            type='number'
            value={formData.duration}
            onChange={handleChange}
            margin='normal'
            InputProps={{ inputProps: { min: 1 } }}
            helperText='If left empty, will be calculated from start and end times'
          />
        </Section>

        <Section>
          <Typography variant='h6'>Contact Information</Typography>
          <TextField
            label='Coach Name'
            name='coachName'
            value={formData.coachName}
            onChange={handleChange}
            fullWidth
            required
            margin='normal'
            error={formData.coachName === ''}
            helperText={
              formData.coachName === '' ? 'Coach Name is required' : ''
            }
          />

          <TextField
            label='Coach Email'
            name='coachEmail'
            type='email'
            value={formData.coachEmail}
            onChange={handleChange}
            fullWidth
            required
            margin='normal'
            error={formData.coachEmail === ''}
            helperText={
              formData.coachEmail === '' ? 'Coach Email is required' : ''
            }
          />
        </Section>

        <Section>
          <Typography variant='h6'>Additional Details</Typography>
          <TextField
            label='Event Description'
            name='body'
            value={formData.body}
            onChange={handleChange}
            fullWidth
            multiline
            rows={4}
            margin='normal'
          />

          <FormControlLabel
            control={
              <Switch
                name='hideAttendees'
                checked={formData.hideAttendees}
                onChange={handleSwitchChange}
                color='primary'
              />
            }
            label='Hide Attendees'
          />
        </Section>

        <Divider sx={{ my: 3 }} />

        {error && <ErrorMessage>{error.message}</ErrorMessage>}

        <Box display='flex' justifyContent='flex-end' mt={2}>
          <Button
            type='submit'
            variant='contained'
            color='primary'
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create Coaching Event'}
          </Button>
        </Box>
      </Form>
    </Container>
  )
}
